import { z } from 'zod';
import { LangCodeSchema, TimestampSchema, UuidSchema } from './primitives';

// =============================================================================
// Enums
// =============================================================================

export const TaskStatusSchema = z.enum(['open', 'in_progress', 'done', 'cancelled']);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

export const TaskPrioritySchema = z.enum(['low', 'medium', 'high', 'urgent']);
export type TaskPriority = z.infer<typeof TaskPrioritySchema>;

// =============================================================================
// Task (row)
// =============================================================================

export const TaskSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  created_by: UuidSchema,
  assignee_id: UuidSchema.nullable(),
  title: z.string().min(1).max(500),
  description: z.string().max(10_000).nullable(),
  source_lang: LangCodeSchema,
  status: TaskStatusSchema,
  priority: TaskPrioritySchema,
  due_at: TimestampSchema.nullable(),
  completed_at: TimestampSchema.nullable(),
  related_entity_type: z.string().max(64).nullable(),
  related_entity_id: UuidSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Task = z.infer<typeof TaskSchema>;

// =============================================================================
// Task create / update inputs
// =============================================================================

export const TaskCreateInputSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().max(10_000).optional(),
  assignee_id: UuidSchema.optional(),
  status: TaskStatusSchema.optional(),
  priority: TaskPrioritySchema.default('medium'),
  due_at: TimestampSchema.optional(),
  related_entity_type: z.string().min(1).max(64).optional(),
  related_entity_id: UuidSchema.optional(),
  tag_ids: z.array(UuidSchema).max(50).optional(),
});
export type TaskCreateInput = z.infer<typeof TaskCreateInputSchema>;

export const TaskUpdateInputSchema = z
  .object({
    title: z.string().min(1).max(500),
    description: z.string().max(10_000).nullable(),
    assignee_id: UuidSchema.nullable(),
    status: TaskStatusSchema,
    priority: TaskPrioritySchema,
    due_at: TimestampSchema.nullable(),
    related_entity_type: z.string().min(1).max(64).nullable(),
    related_entity_id: UuidSchema.nullable(),
  })
  .partial();
export type TaskUpdateInput = z.infer<typeof TaskUpdateInputSchema>;

export const TaskStatusUpdateInputSchema = z.object({
  status: TaskStatusSchema,
});
export type TaskStatusUpdateInput = z.infer<typeof TaskStatusUpdateInputSchema>;

// =============================================================================
// Task tag
// =============================================================================

export const TaskTagSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  name: z.string().min(1).max(100),
  color: z.string().max(16).nullable(),
  created_by: UuidSchema,
  created_at: TimestampSchema,
});
export type TaskTag = z.infer<typeof TaskTagSchema>;

export const TaskTagCreateInputSchema = z.object({
  name: z.string().min(1).max(100),
  color: z.string().max(16).optional(),
});
export type TaskTagCreateInput = z.infer<typeof TaskTagCreateInputSchema>;

export const TaskTagAssignmentSchema = z.object({
  task_id: UuidSchema,
  tag_id: UuidSchema,
  tenant_id: UuidSchema,
});
export type TaskTagAssignment = z.infer<typeof TaskTagAssignmentSchema>;

// =============================================================================
// Task filter (GET /tasks)
// =============================================================================

export const TaskFilterSchema = z
  .object({
    status: TaskStatusSchema,
    priority: TaskPrioritySchema,
    /** 'me' maps to the caller's user id server-side; otherwise accept a uuid. */
    assignee: z.union([z.literal('me'), z.literal('any'), UuidSchema]),
    tag_id: UuidSchema,
    due_before: TimestampSchema,
    due_after: TimestampSchema,
    /** Free-text title search. */
    q: z.string().max(200),
  })
  .partial();
export type TaskFilter = z.infer<typeof TaskFilterSchema>;
